import * as React from 'react'
import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import { rem } from 'polished'
import styled from 'styled-components'
import clsx from 'clsx'
import Group from 'components/utils/Group'
import TextButton from 'LuxKit/Button/TextButton'
import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import PriceRowAgentHubCommission from 'agentHub/components/PriceRowAgentHubCommission'
import PriceRowLuxPlusPrice from 'LuxKit/PricePoints/LuxPlus/PriceRowLuxPlusPrice'
import PriceRowLuxPlusNonMemberPrice from 'LuxKit/PricePoints/LuxPlus/PriceRowLuxPlusNonMemberPrice'
import LoyaltyPointsEarnWithBreakdownModal from 'loyalty/components/Earn/LoyaltyPointsEarnWithBreakdownModal'
import WishlistGuard from 'wishlists/components/WishlistGuard'
import { ExperienceBookmarkButton } from 'wishlists/components/Bookmark/Buttons'
import LoyaltyBurnPriceDisplay from 'loyalty/components/LoyaltyBurnPriceDisplay'
import { mediaQueryUp } from 'components/utils/breakpoint'
import LineShoppingCartIcon from 'LuxKit/Icons/line/LineShoppingCartIcon'
import { getAgentHubExperienceType } from 'lib/agentHub/agentHubUtils'
import AddToWeddingGiftRegistryCartCTA from 'weddings/components/WeddingAddToGiftRegistryCartCTA'
import LoadingSpinner from 'components/Common/Loading/LoadingSpinner'

const FadingPriceRowPriceCaption = styled(PriceRowPriceCaption)`
  opacity: 0;
  transition: opacity 0.2s;
  pointer-events: none;

  &.visible {
    opacity: 1;
    pointer-events: auto;
  }
`

const BottomContent = styled.div`
  display: grid;
  grid-template-areas:
    "save . ."
    "price . ."
    "buy buy buy";
  grid-template-columns: minmax(0, 1fr) auto auto;
  grid-row-gap: ${rem(12)};
  grid-column-gap: ${rem(20)};
  align-items: center;
  ${mediaQueryUp.tablet} {
    grid-template-areas: "price save buy";
    grid-template-columns: minmax(0, 1fr) auto auto;
  }
`

const PriceGridItem = styled.div`
  grid-area: price;
  min-width: 0;
`

const SaveGridItem = styled.div`
  grid-area: save;

  ${mediaQueryUp.tablet} {
    text-align: end;
  }
`

const BuyGridItem = styled.div`
  grid-area: buy;
`

interface Props {
  experience: App.ExperienceOffer
  totalPrice: number
  totalLuxPlusPrice: number
  isFromPrice: boolean
  showLuxPlusPrice: boolean
  pointsEarnCalculationRequest: Array<App.LoyaltyPointsEarnCalculationRequest | undefined>
  /** Points the user has chosen to burn against totalPrice/totalLuxPlusPrice, if any */
  pointsToBurn?: number
  /** Full points value of totalPrice/totalLuxPlusPrice, used to scale the burn discount */
  burnPriceInPoints?: number
  pointsHideZeroCash?: boolean
  userMultiItemCartSupported: boolean
  isWeddingGiftRegistry?: boolean
  isReservingTickets?: boolean
  onBuyNow: (e: React.MouseEvent<HTMLElement>) => void
  onAddToUserCart: (e: React.MouseEvent<HTMLElement>) => void
  onAddToWeddingGiftRegistryCart: (e: React.MouseEvent<HTMLElement>) => void
  secondaryAction?: React.ReactElement
}

function ExperienceOfferPageBuyBar(props: Props) {
  const {
    experience,
    totalPrice,
    totalLuxPlusPrice,
    isFromPrice,
    showLuxPlusPrice,
    pointsEarnCalculationRequest,
    pointsToBurn,
    burnPriceInPoints,
    pointsHideZeroCash,
    userMultiItemCartSupported,
    isWeddingGiftRegistry = false,
    isReservingTickets = false,
    onBuyNow,
    onAddToUserCart,
    onAddToWeddingGiftRegistryCart,
    secondaryAction,
  } = props
  const intl = useIntl()
  const productType = useMemo(() => getAgentHubExperienceType(experience), [experience])
  const offerLocations = useMemo(() => [experience.location.name], [experience.location.name])

  const fromLabel = intl.formatMessage({
    id: 'GNhke_zVHO9zGHOsqLCn',
    defaultMessage: 'From',
  })
  const totalLabel = intl.formatMessage({
    id: 'MGi3hnHzfEbY4NyUM2_2',
    defaultMessage: 'total',
  })
  const addToCartLabel = intl.formatMessage({
    id: 'sLMdZIi4tcR7FUyZ77YF',
    defaultMessage: 'Add to cart',
  })
  const buyNowLabel = intl.formatMessage({
    id: 'Rl_hV-p1C684QVEDFNiK',
    defaultMessage: 'Buy now',
  })

  const isBurnActive = pointsToBurn !== undefined && burnPriceInPoints !== undefined
  const saleUnit = (experience.ticketUnitLabel && isFromPrice) ? experience.ticketUnitLabel : totalLabel

  return (
    <BottomContent>
      <PriceGridItem>
        <Group direction="vertical" gap={4}>
          {!showLuxPlusPrice && (
            <Group direction="vertical">
              <FadingPriceRowPriceCaption className={clsx({ visible: isFromPrice })}>
                {fromLabel}
              </FadingPriceRowPriceCaption>
              {isBurnActive && (
                <LoyaltyBurnPriceDisplay
                  size="M"
                  totalPrice={totalPrice}
                  pointsToBurn={pointsToBurn}
                  priceInPoints={burnPriceInPoints}
                  hideZeroCash={pointsHideZeroCash}
                  saleUnit={saleUnit}
                  mode="regular-only"
                />
              )}
              {!isBurnActive && (
                <PriceRowPrice
                  size="M"
                  price={totalPrice}
                  saleUnit={saleUnit}
                />
              )}
              <PriceRowAgentHubCommission
                offerId={experience.id}
                productType={productType}
                offerLocations={offerLocations}
              />
            </Group>
          )}
          {!!showLuxPlusPrice && (
            <Group direction="vertical">
              <FadingPriceRowPriceCaption className={clsx({ visible: isFromPrice })}>
                {fromLabel}
              </FadingPriceRowPriceCaption>
              {isBurnActive && (
                <LoyaltyBurnPriceDisplay
                  size="L"
                  totalPrice={totalPrice}
                  memberPrice={totalLuxPlusPrice}
                  pointsToBurn={pointsToBurn}
                  priceInPoints={burnPriceInPoints}
                  hideZeroCash={pointsHideZeroCash}
                  saleUnit={saleUnit}
                  mode="member-prominent"
                />
              )}
              {!isBurnActive && <>
                <PriceRowLuxPlusPrice
                  logoPlacement="trailing"
                  size="L"
                  memberPrice={totalLuxPlusPrice}
                  saleUnit={saleUnit}
                />
                <PriceRowLuxPlusNonMemberPrice
                  nonMemberPrice={totalPrice}
                />
              </>}
            </Group>
          )}
          <LoyaltyPointsEarnWithBreakdownModal
            calculationRequests={pointsEarnCalculationRequest}
            calculationType={isFromPrice ? 'estimate' : 'exact'}
            hideStatusCredits
            offer={experience}
            pointsToApply={pointsToBurn}
            burnPriceInPoints={burnPriceInPoints}
            showBurnAdjustedBreakdown
          />
        </Group>
      </PriceGridItem>
      <WishlistGuard>
        <SaveGridItem>
          <ExperienceBookmarkButton offer={experience} />
        </SaveGridItem>
      </WishlistGuard>
      <BuyGridItem>
        <Group direction="vertical-reverse" gap={8} tabletDirection="horizontal">
          {isWeddingGiftRegistry && userMultiItemCartSupported && (
            <AddToWeddingGiftRegistryCartCTA
              size="large"
              onClick={onAddToWeddingGiftRegistryCart}
              disabled={experience.status !== 'ONLINE'}
            />
          )}
          {!isWeddingGiftRegistry && (
            <>
              {userMultiItemCartSupported && <TextButton
                kind="secondary"
                startIcon={<LineShoppingCartIcon />}
                size="large"
                onClick={onAddToUserCart}
                disabled={experience.status !== 'ONLINE' || isReservingTickets}
              >
                {addToCartLabel}
              </TextButton>}
              {secondaryAction}
              <TextButton
                kind="primary"
                size="large"
                type="button"
                startIcon={isReservingTickets ? <LoadingSpinner size={24} /> : false}
                onClick={onBuyNow}
                disabled={experience.status !== 'ONLINE' || isReservingTickets}
              >
                {buyNowLabel}
              </TextButton>
            </>
          )}
        </Group>
      </BuyGridItem>
    </BottomContent>
  )
}

export default ExperienceOfferPageBuyBar
